import { useMemo } from 'react';
import {
  rescoreStocks,
  ALL_QUALITY_KEYS,
  ALL_VALUATION_KEYS,
} from '@/lib/clientScore';
import { projectStocks, ProjectionHorizon } from '@/lib/projectScore';
import type { StockFactors, PillarScores, ScoredStock } from '@/lib/compute';

interface DataLike {
  stocks: ScoredStock[];
}

interface Args {
  data: DataLike | null | undefined;
  enabledFactors: Set<keyof StockFactors>;
  defaultAllSize: number;
  pillarWeights: Record<keyof PillarScores, number>;
  defaultWeights: Record<keyof PillarScores, number>;
  projectionHorizon: ProjectionHorizon;
}

interface Result {
  /** Sector taxonomy derived from `data.stocks`. */
  sectors: string[];
  industriesBySector: Record<string, string[]>;
  /** Quality factor subset whose factor toggles are currently on. */
  enabledQuality: Set<keyof StockFactors>;
  enabledValuation: Set<keyof StockFactors>;
  /** True when pillar weights are non-default. */
  weightsChanged: boolean;
  /** True when either factor toggles or pillar weights diverge from defaults. */
  needsRescore: boolean;
  /** Stocks rescored client-side with the user's enabled factors + pillar
   *  weights, or null when no rescore needed. */
  rescored: ScoredStock[] | null;
  /** Either `rescored` or the raw scores from `data.stocks`. */
  baseStocks: ScoredStock[];
  /** `baseStocks` projected to the chosen horizon, or null at "now". */
  projectedStocks: ScoredStock[] | null;
  /** Final pipeline output — what every downstream selector consumes. */
  stocksToUse: ScoredStock[];
}

/**
 * Pull the entire scoring/projection pipeline out of app/page.tsx so the page
 * component owns only state + render. Inputs: raw data + the user's factor
 * toggles + pillar weights + projection horizon. Output: the final
 * `stocksToUse` array plus all the intermediate derivations downstream
 * selectors need (sectors, weightsChanged, etc.).
 */
export function useStocksToUse(args: Args): Result {
  const {
    data,
    enabledFactors,
    defaultAllSize,
    pillarWeights,
    defaultWeights,
    projectionHorizon,
  } = args;

  const sectors = useMemo(() => {
    if (!data) return [] as string[];
    return [...new Set(data.stocks.map((s) => s.sector))].sort().concat(['Canada']);
  }, [data]);

  const industriesBySector = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const s of data?.stocks ?? []) {
      const sec = s.sector ?? 'Unknown';
      const ind = s.industry ?? 'Unknown';
      (map[sec] ??= new Set()).add(ind);
    }
    const out: Record<string, string[]> = {};
    for (const [sec, set] of Object.entries(map)) {
      out[sec] = [...set].sort();
    }
    return out;
  }, [data]);

  const enabledQuality = useMemo(() => {
    const s = new Set<keyof StockFactors>();
    for (const k of ALL_QUALITY_KEYS) if (enabledFactors.has(k)) s.add(k);
    return s;
  }, [enabledFactors]);

  const enabledValuation = useMemo(() => {
    const s = new Set<keyof StockFactors>();
    for (const k of ALL_VALUATION_KEYS) if (enabledFactors.has(k)) s.add(k);
    return s;
  }, [enabledFactors]);

  const weightsChanged = useMemo(
    () =>
      (Object.keys(pillarWeights) as (keyof PillarScores)[]).some(
        (k) => pillarWeights[k] !== defaultWeights[k],
      ),
    [pillarWeights, defaultWeights],
  );

  const needsRescore =
    enabledFactors.size !== defaultAllSize || weightsChanged;

  const rescored = useMemo(() => {
    if (!data || !needsRescore) return null;
    return rescoreStocks(
      data.stocks,
      enabledQuality,
      enabledValuation,
      weightsChanged ? pillarWeights : undefined,
    );
  }, [data, needsRescore, enabledQuality, enabledValuation, weightsChanged, pillarWeights]);

  const baseStocks = useMemo(
    () => rescored ?? data?.stocks ?? [],
    [rescored, data],
  );

  const projectedStocks = useMemo(() => {
    if (projectionHorizon === 'now' || baseStocks.length === 0) return null;
    return projectStocks(
      baseStocks,
      projectionHorizon,
      needsRescore ? enabledQuality : undefined,
      needsRescore ? enabledValuation : undefined,
      weightsChanged ? pillarWeights : undefined,
    );
  }, [baseStocks, projectionHorizon, needsRescore, enabledQuality, enabledValuation, weightsChanged, pillarWeights]);

  const stocksToUse = projectedStocks ?? baseStocks;

  return {
    sectors,
    industriesBySector,
    enabledQuality,
    enabledValuation,
    weightsChanged,
    needsRescore,
    rescored,
    baseStocks,
    projectedStocks,
    stocksToUse,
  };
}
